/* eslint-disable max-lines */
// deps
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";

// internal-deps
import {
    isAndroidPlatform,
    isAppPlatform,
    isEmpty,
    isiOSPlatform,
    isWebPlatform,
    safeJsonParse,
    useEffectOnce,
} from "@swiggy-private/core-ui";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";

// helpers
import { logDealPurchaseEvent, logTableReservationShareEvent } from "@dineout-payment-success-ui/helpers/Clevertap";
import { isEventOrganisedByRX } from "@dineout-payment-success-ui/helpers/EventBooking";
import { navigateToLink, openHelpWebView } from "@dineout-payment-success-ui/helpers/nativeBridge";
import {
    getColorCode,
    getDOPaymentSuccessData,
    getListItemProps,
    checkIsTableReservation,
    getTicketDetailsData,
    getEventBookingShareText,
    getAggregatedItems,
    getTotalBill,
    isShowBillWidget,
    getTableBookingShareText,
    getConditionalProps,
} from "@dineout-payment-success-ui/helpers/index";
import { isDatingFlow, getDatingFlowData } from "@swiggy-private/checkout-core";
import Blaze from "@swiggy-private/blaze.js";
import { format, getCloudinaryUrl } from "@swiggy-private/js-utils";

// types
import { IDineoutPaymentSuccess, IMetaData, ITicketAttributes, IItem } from "@dineout-payment-success-ui/interfaces";
import {
    BLAZE_EVENTS,
    CheckoutContext,
    CheckoutRoute,
    closeWebview,
    DineoutSuccessActionPayload,
    getBridgeSdk,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    useCustomHardwareBack,
} from "@swiggy-private/checkout-core";
import { ExpendableType } from "@swiggy-private/dls-web-core/dist/components/ListView/types";
import type { RawOrderData } from "@swiggy-private/native-bridge";

// constants
import {
    APP_BANNER_PROPS,
    LIST_TITLES,
    BILL_TEXTS,
    EVENTS_HOMEPAGE_DEEPLINK,
    BILL_ELEMENT_ENTRIES,
    EVENT_BOOKING_SHARE_TITLE,
    DEAL_BOOKING_SHARE_TITLE,
    ALLOWED_DEAL_CATEGORIES_FOR_SHARE,
    DealCategory,
    BOOKING_STATUS,
    LOCATION_URLS,
    CALL_URL,
    HELP_CTA_TEXT,
    DINEOUT_HOMEPAGE_WITH_REFRESH_DEEPLINK,
    DATING_DEEPLINK_PREFIX,
} from "@dineout-payment-success-ui/constants";
import { getCalloutsBannerVisibilityState } from "@dineout-payment-success-ui/helpers/CalloutsBanner";
import { LOTTIE_ANIMATIONS } from "@dineout-payment-success-ui/constants/LottieAnimations";
import { isCelebrationWindowActive } from "@dineout-payment-success-ui/helpers/LottieHelper";

// components
import { Header } from "./components/Header";
import { WhatToDoNext } from "./components/WhatToDoNext";
import { Footer } from "./components/payment-success-footer";
import { BillDetails } from "./components/payment-success-bills-details";
import { HalfCardContent } from "./components/TicketDetailsInfoHalfCard";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as ShareIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/iosShare/20";
import { default as ArrowBack } from "@swiggy-private/dls-web-icons/dist/web/generic/arrowBack/20";
import { default as HelpIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chat/20";

import ListView from "@swiggy-private/dls-web-core/dist/components/ListView";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { default as Arrow20 } from "@swiggy-private/dls-web-icons/dist/web/generic/arrowFront/20";
import { CartItemListRenderer } from "@swiggy-private/dls-web-recipe-do-web/dist/components/CartItemListRenderer";
import { TableBookingInfoTicket } from "@swiggy-private/dls-web-recipe-do-web/dist/components/TableBookingInfoTicket";
import { TableBookingStatusBanner } from "./components/BookingStatusBanner";
import { EventBookingStatusBanner } from "./components/BookingStatusBanner";
import { SavingsCard } from "./components/payment-success-savings-card";
import TableReservationCelebrationHalfCard from "./components/TableReservationCelebrationHalfCard";

// hooks
import { useDineoutBookingSuccess } from "@dineout-payment-success-ui/hooks/useDineoutBookingSuccess";
import { usePartialPaymentCtaClick } from "@dineout-payment-success-ui/hooks/usePartialPaymentCtaClick";

// styles
import {
    AnimatedScrollableContent,
    ContentDiv,
    DineoutWrapper,
    ScrollableContent,
    BillWrapper,
    ListViewWrapper,
    ListTextWrapper,
    HeaderTicketWrapper,
    TicketDetailsWrapper,
    HeaderWrapper,
    HelpIconWrapper,
    BackIconWrapper,
    ShareIconWrapper,
    ViewRestaurantCtaWrapper,
    HeaderRightSectionWrapper,
    CommsTextBannerWrapper,
    ToastContainer,
    DividerWrapper,
    InfoTicketWrapper,
    HalfCardPortalContainer,
    DinecashPoolBanner,
    HalfCardPortalContainerWrapper,
} from "./styles";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import CancelBookingWidget from "./components/cancel-booking-widget";
import { CalloutsBanner } from "./components/CalloutsBanner";
import { CommsTextBanner } from "./components/comms-text-banner";
import { ITableReservationSuccessPresentationalMeta } from "@dineout-payment-success-ui/interfaces/TableReservation";
import { FooterImage } from "./components/FooterImage";
import { SUCCESS_PAGE_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface ITableReservationSuccessPageProps extends IDineoutPaymentSuccess {
    isEventReservation: boolean;
    reloadSuccessPage: VoidFunction;
    presentationalMeta: ITableReservationSuccessPresentationalMeta;
    nativeBridgeCalledRef: React.MutableRefObject<boolean>;
}

const TableReservationSuccessPage: React.FC<ITableReservationSuccessPageProps> = (props) => {
    const { checkoutState } = useContext(CheckoutContext);
    const [softError, setSoftError] = useState<string | undefined>();
    const [isScrolled, setIsScrolled] = useState(false);
    const {
        isEventReservation,
        reloadSuccessPage,
        presentationalMeta: transformedResponse,
        nativeBridgeCalledRef,
    } = props;
    const {
        bookingDetails,
        restaurantDetails,
        ticketAttributes,
        cancelBookingMeta,
        viewRestaurantCtaMeta,
        ctas,
        whatToDoNextSrc,
        commsTextBannerProps,
        ctaSubTextMeta,
        bookingSuccessBanner,
        rewardsWidget,
        rewardsPoolWidget,
        calloutsBanner,
    } = transformedResponse;
    const orderData = props?.data?.orders?.[0];
    const confirmOrderData = orderData?.orderJobs?.[0];
    const metaDataString = confirmOrderData?.metadata || "{}";
    const metaDataJson = safeJsonParse<IMetaData>(metaDataString);
    const [showPassDetailsCard, setShowPassDetailsCard] = useState(false);
    const [safeAreaBottomPx, setSafeAreaBottomPx] = useState<number>(0);
    const [halfCardData, setHalfCardData] = useState<{ heading: string; subheadings: string[] }>();
    const isCheckoutFlow = useMemo(() => {
        return Boolean((checkoutState.payload as DineoutSuccessActionPayload)?.isCheckoutFlow);
    }, [checkoutState?.payload]);

    const shouldShowCelebration = useMemo(() => {
        const isCelebrationActive = isCelebrationWindowActive();
        const isEligibleForCelebration =
            !isEventReservation || (metaDataJson?.storeInfo ? isEventOrganisedByRX(metaDataJson.storeInfo) : false);
        const isOrderNotCancelled = metaDataJson?.items?.[0]?.bookingStatus !== BOOKING_STATUS.CANCELLED;

        return isCelebrationActive && isOrderNotCancelled && isCheckoutFlow && isEligibleForCelebration;
    }, [isEventReservation, isCheckoutFlow, metaDataJson]);

    const [showCelebrationHalfCard, setShowCelebrationHalfCard] = useState(shouldShowCelebration);
    const { ticketCountTitle, ticketDateTitle, ticketTimeTitle, ticketTitle } = getAggregatedItems(
        metaDataJson?.aggregatedItemsInfo,
    );
    const totalBillAmount = useMemo(() => getTotalBill(metaDataJson), [metaDataJson]);
    const isTableReservation = useMemo(
        () => checkIsTableReservation(metaDataJson?.dealCategory, metaDataJson?.type),
        [metaDataJson?.dealCategory, metaDataJson?.type],
    );
    const isPartialPayment = useMemo(
        () => metaDataJson?.ctx?.isPartialPaymentApplied || false,
        [metaDataJson?.ctx?.isPartialPaymentApplied],
    );

    // This is specific to event booking flow
    const bookingStatusBannerSubtitle = useMemo(
        () => metaDataJson?.ctx?.bookingStatusBannerSubtitle || null,
        [metaDataJson?.ctx?.bookingStatusBannerSubtitle],
    );
    const completePaymentCta = useMemo(() => metaDataJson?.ctx?.payCta || null, [metaDataJson?.ctx?.payCta]);

    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform);
    const isWebFlow = isWebPlatform(platform);
    const isAppFlow = isAppPlatform(platform);

    const restaurantId = useMemo(() => restaurantDetails?.id || "", [restaurantDetails?.id]);

    const { billDetailsInfo, dealCategory, itemsData, savingCards } = getDOPaymentSuccessData(
        metaDataString,
        isTableReservation,
    );

    const getEventId = useCallback((itemsData?: IItem[]): string => {
        const itemId = itemsData?.[0]?.itemId;
        const eventId = itemId?.split("-")[1];
        if (isEmpty(eventId)) {
            return itemId || "";
        }
        return eventId || "";
    }, []);

    const eventId = useMemo(() => getEventId(itemsData), [getEventId, itemsData]);
    const bookingId = useMemo(() => orderData?.orderId, [orderData]);
    const eventContext = useMemo(
        () => ({
            orderId: orderData?.orderId || "",
            restaurantId: restaurantId,
            eventId,
            dealCategory,
            guests: bookingDetails?.noOfGuests || 0,
        }),
        [orderData?.orderId, restaurantId, eventId, dealCategory, bookingDetails?.noOfGuests],
    );

    const bookingStatus = useMemo(() => metaDataJson?.items?.[0]?.bookingStatus, [metaDataJson?.items]);
    const showBillWidget = useMemo(
        () => isShowBillWidget(totalBillAmount || 0, dealCategory),
        [dealCategory, totalBillAmount],
    );
    const ticketDetailsData = useMemo(() => getTicketDetailsData(itemsData), [itemsData]);

    const qrCode = useMemo(() => metaDataJson?.ctx?.qrCode, [metaDataJson?.ctx?.qrCode]);

    const shouldShowShareIcon =
        useMemo(() => ALLOWED_DEAL_CATEGORIES_FOR_SHARE.includes(dealCategory as DealCategory), [dealCategory]) &&
        !isWebFlow &&
        bookingStatus != BOOKING_STATUS.CANCELLED;

    const handleInfoClick = useCallback((ticketName: string, tncList: string[]) => {
        setHalfCardData({
            heading: ticketName,
            subheadings: tncList,
        });
        setShowPassDetailsCard(true);
    }, []);

    const hidePassDetailsCard = () => {
        setShowPassDetailsCard(false);
    };

    const hideCelebrationHalfCard = useCallback(() => {
        setShowCelebrationHalfCard(false);
    }, []);

    const {
        onClickViewHelpDineoutBookingSuccess,
        onImpressionDineoutBookingSuccess,
        onClickDoneDineoutBookingSuccess,
        onBillDetailsImpressionDineoutBookingSuccess,
        onImpressionDownloadCTABookingSuccess,
        onClickDownloadCTABookingSuccess,
        onClickDoneEventCTA,
        onImpressionQrCode,
        onClickShareIcon,
        onScreenViewBookingSuccess,
        onImpressionEventNudge,
        onClickCancelBookingPopupCta,
        onClickCallRestaurantCTA,
        onClickTnCDineoutBookingSuccess,
        onClickViewRestaurantDetails,
        onClickMapsCTA,
        onClickCancelBookingCta,
        onClickBookingTicketCta,
        onClickBackButtonBookingDetails,
        onImpressionBenefitCallout,
        onClickThingsToRemember,
        onImpressionBookingCard,
        onImpressionYourBookings,
        onImpressionCashBackBenefitCallout,
        onImpressionDinecashPoolBanner,
    } = useDineoutBookingSuccess();

    const onClickShareIconButton = useCallback(() => {
        onClickShareIcon(isCheckoutFlow, eventContext);

        const bookingInfo = {
            date: bookingDetails?.date,
            timeString: bookingDetails?.time,
            guestCount: bookingDetails?.noOfGuests,
            offerText: ticketAttributes?.offerDetails?.title,
        };

        const { message, deeplink } = isEventReservation
            ? getEventBookingShareText(eventId, restaurantDetails, ticketTitle, bookingDetails?.date)
            : getTableBookingShareText(restaurantDetails, bookingInfo);

        // Log Clevertap event for share button click only for table reservations
        if (!isEventReservation) {
            logTableReservationShareEvent(bookingId || "", restaurantId || "", bookingDetails?.noOfGuests || 0);
        }

        getBridgeSdk()
            .shareOnSocialMedia({
                title: isEventReservation ? EVENT_BOOKING_SHARE_TITLE : DEAL_BOOKING_SHARE_TITLE,
                message,
                url: deeplink,
            })
            .catch(() => void 0);
    }, [
        onClickShareIcon,
        isCheckoutFlow,
        eventContext,
        bookingDetails?.date,
        bookingDetails?.time,
        bookingDetails?.noOfGuests,
        ticketAttributes?.offerDetails?.title,
        isEventReservation,
        eventId,
        restaurantDetails,
        ticketTitle,
        bookingId,
        restaurantId,
    ]);

    const onClickBack = useCallback(() => {
        onClickBackButtonBookingDetails();
        closeWebview();
    }, [onClickBackButtonBookingDetails]);

    const shouldRedirectToHomePage = useCallback(() => {
        const isFromDatingFlow = isDatingFlow();
        const { referrerId } = getDatingFlowData();

        let navigationLink;
        if (isFromDatingFlow) {
            navigationLink = format(DATING_DEEPLINK_PREFIX, referrerId);
        } else {
            navigationLink = isEventReservation ? EVENTS_HOMEPAGE_DEEPLINK : DINEOUT_HOMEPAGE_WITH_REFRESH_DEEPLINK;
        }

        navigateToLink(navigationLink, true, true);
    }, [isEventReservation]);

    useCustomHardwareBack([CheckoutRoute.DINEOUT_PAYMENT_SUCCESS], shouldRedirectToHomePage);

    const navigateToDineoutHomePage = useCallback(() => {
        if (isEventReservation) {
            onClickDoneEventCTA(isCheckoutFlow, eventContext);
        } else {
            onClickDoneDineoutBookingSuccess(orderData?.orderId || "", isCheckoutFlow, dealCategory);
        }
        shouldRedirectToHomePage();
    }, [
        dealCategory,
        eventContext,
        isCheckoutFlow,
        isEventReservation,
        onClickDoneDineoutBookingSuccess,
        onClickDoneEventCTA,
        orderData?.orderId,
        shouldRedirectToHomePage,
    ]);

    const handleViewVenueDetails = useCallback(() => {
        if (viewRestaurantCtaMeta?.link) {
            navigateToLink(viewRestaurantCtaMeta.link, false);
        }
        onClickViewRestaurantDetails(isCheckoutFlow, { orderId: orderData?.orderId || "" });
    }, [isCheckoutFlow, onClickViewRestaurantDetails, orderData?.orderId, viewRestaurantCtaMeta?.link]);

    const handleBookingCtaClick = useCallback(
        (ctaTitle: string, ctaDeeplink: string) => {
            onClickBookingTicketCta(isCheckoutFlow, {
                ctaTitle,
                ctaDeeplink,
                orderId: orderData?.orderId || "",
                dealCategory,
            });
            navigateToLink(ctaDeeplink, false);
        },
        [dealCategory, isCheckoutFlow, onClickBookingTicketCta, orderData?.orderId],
    );

    const handleViewHelp = useCallback(() => {
        onClickViewHelpDineoutBookingSuccess(orderData?.orderId || "", isCheckoutFlow);
        openHelpWebView(orderData?.orderId || "", restaurantId || "", isEventReservation);
    }, [onClickViewHelpDineoutBookingSuccess, orderData?.orderId, restaurantId, isCheckoutFlow, isEventReservation]);

    const getThingsToRemember = useCallback(
        (thingsToRemember: string[]) => (
            <ListTextWrapper variant="V2.body3.neutral.regular" color="text_Med_Emphasis">
                <ol>
                    {thingsToRemember.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ol>
            </ListTextWrapper>
        ),
        [],
    );
    const getTnC = useCallback(
        (tnc: string) => (
            <ListTextWrapper
                variant="V2.body3.neutral.regular"
                color="text_Med_Emphasis"
                dangerouslySetInnerHTML={{
                    __html: tnc,
                }}
            />
        ),
        [],
    );

    const getListItems = useCallback(
        (ticketAttributes: ITicketAttributes) => {
            const listItems = [];

            const analyticsPayload = {
                orderId: orderData?.orderId || "",
                restaurantId: restaurantId,
                guests: bookingDetails?.noOfGuests || 0,
                dealCategory,
            };
            if (!isEmpty(ticketAttributes?.thingsToRemember)) {
                listItems.push({
                    ...getListItemProps(LIST_TITLES.TTR, () =>
                        onClickThingsToRemember(isCheckoutFlow, {
                            ...analyticsPayload,
                        }),
                    ),
                    children: getThingsToRemember(ticketAttributes?.thingsToRemember),
                });
            }
            if (!isEmpty(ticketAttributes?.tnc)) {
                listItems.push({
                    ...getListItemProps(LIST_TITLES.TNC, () =>
                        onClickTnCDineoutBookingSuccess(isCheckoutFlow, analyticsPayload),
                    ),
                    children: getTnC(ticketAttributes?.tnc),
                });
            }
            return listItems;
        },
        [
            bookingDetails?.noOfGuests,
            dealCategory,
            getThingsToRemember,
            getTnC,
            isCheckoutFlow,
            onClickThingsToRemember,
            onClickTnCDineoutBookingSuccess,
            orderData?.orderId,
            restaurantId,
        ],
    );

    const onBillDetailsImpression = useCallback(() => {
        const ids = { restaurantId: restaurantDetails?.id || "", eventId, bookingId };
        const guestCount = ticketDetailsData?.reduce((acc, ticket) => acc + (ticket.quantity || 0), 0);
        const ticketDetails = ticketDetailsData?.map((ticket) => ({
            [String(ticket?.itemVariantId)]: ticket.quantity,
        })) as unknown as Record<string, number>;
        const eventData = { ticketDetails, eventId, guestCount, isEventReservation, dealCategory };

        onBillDetailsImpressionDineoutBookingSuccess({
            ids,
            bookingDetails,
            billDetailsInfo,
            eventData,
            isCheckoutFlow,
        });
    }, [
        billDetailsInfo,
        bookingDetails,
        bookingId,
        dealCategory,
        eventId,
        isCheckoutFlow,
        isEventReservation,
        onBillDetailsImpressionDineoutBookingSuccess,
        restaurantDetails?.id,
        ticketDetailsData,
    ]);

    useEffectOnce(() => {
        Blaze.recordEnd(BLAZE_EVENTS.RECORD_DO_PAYMENT_SUCCESS_SCREEN_LOAD_TIME);
        recordNREvent({
            eventType: NEWRELIC_TABLES.DO_PAYMENT_SUCCESS_PAGE_VIEW,
        });
        const billAmount = billDetailsInfo?.find(
            (bill: { id: string }) => bill.id === BILL_ELEMENT_ENTRIES.BILL_TO_PAY_ID,
        )?.rightElement?.text;

        if (showCelebrationHalfCard) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TABLE_RESERVATION_CELEBRATION_HALFCARD_IMPRESSION,
                isEventReservation: String(isEventReservation),
                dealCategory: metaDataJson?.dealCategory ?? "",
            });
        }

        if (!isEventReservation) {
            if (!isEmpty(orderData?.orderId)) {
                logDealPurchaseEvent({
                    orderId: orderData?.orderId || "",
                    dealDataForEvent: {
                        amount: totalBillAmount?.toString(),
                        offerLogo: {
                            text: ticketAttributes?.offerDetails?.title,
                        },
                        guestCount: bookingDetails?.noOfGuests,
                        restaurantId,
                        restaurantInfo: {
                            name: restaurantDetails?.name,
                            locality: restaurantDetails?.locality,
                        },
                    },
                    dealCategory: dealCategory as DealCategory,
                    epochTimeOfBookedSlot: metaDataJson?.items[0]?.reservationTimestamp,
                    cxReadableDate: bookingDetails?.date,
                    dealDetailsDeeplink: viewRestaurantCtaMeta?.link,
                });
                onImpressionDineoutBookingSuccess(isCheckoutFlow, { orderId: orderData?.orderId || "", bookingStatus });
            }
            onImpressionBookingCard(isCheckoutFlow, {
                bookingStatus,
                orderId: bookingId || "",
                dealCategory,
                flow: "",
            });

            onImpressionYourBookings(isCheckoutFlow, {
                offer: ticketAttributes?.offerDetails?.title,
                orderId: bookingId || "",
                dealCategory,
                restaurantId: restaurantId || "",
                city: restaurantDetails?.city || "",
            });
        }
        if (!isEventReservation && !isEmpty(ticketAttributes?.offerDetails?.title)) {
            onImpressionBenefitCallout(isCheckoutFlow, { offerText: ticketAttributes?.offerDetails?.title as string });
        }
        if (!isEmpty(rewardsWidget)) {
            onImpressionCashBackBenefitCallout({
                bannerText: rewardsWidget?.title || "",
                dealCategory,
                restaurantId: restaurantId || "",
            });
        }
        if (isEventReservation) {
            onScreenViewBookingSuccess({
                restId: restaurantId,
                eventId,
                dealCategory,
                billAmount,
            });

            onImpressionEventNudge(eventContext);

            if (!isEmpty(qrCode)) {
                onImpressionQrCode(eventContext);
            }
        }

        try {
            // this NB call is for showing dynamic island on iOS
            if (
                !isEmpty(props.data) &&
                isiOSPlatform(platform) &&
                !isEventReservation &&
                (isCheckoutFlow || bookingStatus === BOOKING_STATUS.CANCELLED) &&
                !nativeBridgeCalledRef.current
            ) {
                nativeBridgeCalledRef.current = true;
                getBridgeSdk().storeOrderInfoEvent(props.data as unknown as RawOrderData);
            }
        } catch {
            // no-op
        }

        try {
            const root = document.documentElement;
            const raw = getComputedStyle(root).getPropertyValue("--sab").trim();
            if (raw) {
                const value = parseFloat(raw.endsWith("px") ? raw.slice(0, -2) : raw);
                if (!Number.isNaN(value)) {
                    setSafeAreaBottomPx(value);
                }
            }
        } catch {
            // no-op
        }
    });

    const resetSoftError = useCallback(() => {
        setSoftError(undefined);
    }, []);

    const navigateToCallIntent = useCallback(
        (mobileNumber: string) => {
            onClickCallRestaurantCTA(isCheckoutFlow, { orderId: orderData?.orderId || "", dealCategory });
            navigateToLink(format(CALL_URL, mobileNumber), false);
        },
        [dealCategory, isCheckoutFlow, onClickCallRestaurantCTA, orderData?.orderId],
    );

    const navigateToLocation = useCallback(
        (latitude?: number, longitude?: number, address?: string) => {
            onClickMapsCTA(isCheckoutFlow, { orderId: orderData?.orderId || "", dealCategory });
            const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform);
            const label = encodeURIComponent(address as string);

            let url = "";
            if (isiOSPlatform(platform)) {
                url = format(LOCATION_URLS.IOS, label, latitude, longitude);
            } else if (isAndroidPlatform(platform)) {
                url = format(LOCATION_URLS.ANDROID, label, latitude, longitude);
            }
            getBridgeSdk()
                .launchBrowser({ url })
                .catch(() => void 0);
        },

        [dealCategory, isCheckoutFlow, onClickMapsCTA, orderData?.orderId],
    );

    const handlePaymentCtaClick = usePartialPaymentCtaClick(bookingId);

    const transformedCtas = ctas?.map((cta) => ({
        ...cta,
        onClick: () => cta?.deeplink && handleBookingCtaClick(cta.text, cta.deeplink),
    }));

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const scrollTop = e.currentTarget.scrollTop;
        setIsScrolled(scrollTop > 0);
    }, []);

    const shouldShowCallAction = useMemo(
        () => restaurantDetails?.phoneNumber && !isWebFlow,
        [restaurantDetails?.phoneNumber, isWebFlow],
    );

    const shouldShowLocationAction = useMemo(
        () => restaurantDetails?.latitude && restaurantDetails?.longitude && !isWebFlow,
        [restaurantDetails?.latitude, restaurantDetails?.longitude, isWebFlow],
    );

    const calloutsBannerVisibilityState = useMemo(
        () => getCalloutsBannerVisibilityState(isEventReservation, calloutsBanner, rewardsPoolWidget),
        [isEventReservation, calloutsBanner, rewardsPoolWidget],
    );

    useEffect(() => {
        if (
            !isEventReservation &&
            !isEmpty(rewardsPoolWidget) &&
            !isEmpty(orderData?.orderId) &&
            !isEmpty(restaurantId)
        ) {
            onImpressionDinecashPoolBanner({ orderId: orderData?.orderId || "", restaurantId: restaurantId || "" });
        }
    }, [isEventReservation, rewardsPoolWidget, orderData?.orderId, restaurantId, onImpressionDinecashPoolBanner]);

    return (
        <DineoutWrapper onScroll={handleScroll}>
            <AnimatedScrollableContent>
                <ScrollableContent skipTopPadding isApp={!isWebFlow} skipHorizontalPadding>
                    <ContentDiv>
                        <HeaderWrapper isScrolled={isScrolled}>
                            {!isCheckoutFlow && (
                                <BackIconWrapper>
                                    <GenericIcon
                                        Icon={ArrowBack}
                                        fillColor="text_Button_Tertiary_Low"
                                        onClick={onClickBack}
                                    />
                                </BackIconWrapper>
                            )}
                            <HeaderRightSectionWrapper>
                                {shouldShowShareIcon && (
                                    <ShareIconWrapper>
                                        <GenericIcon
                                            Icon={ShareIcon}
                                            fillColor="text_Button_Tertiary_Low"
                                            onClick={onClickShareIconButton}
                                        />
                                    </ShareIconWrapper>
                                )}
                                {!isCheckoutFlow && (
                                    <HelpIconWrapper onClick={handleViewHelp}>
                                        <GenericIcon Icon={HelpIcon} fillColor="text_Button_Tertiary_Low" />
                                        <Text variant="V2.overline.neutral.medium" color="text_Button_Tertiary_Low">
                                            {HELP_CTA_TEXT}
                                        </Text>
                                    </HelpIconWrapper>
                                )}
                            </HeaderRightSectionWrapper>
                        </HeaderWrapper>
                        {bookingStatus && !isEventReservation && (
                            <TableBookingStatusBanner
                                bannerImage={{
                                    src: bookingSuccessBanner?.bannerImageMeta?.src || "",
                                    isBackgroundImageWithContent:
                                        bookingSuccessBanner?.bannerImageMeta?.isGridWidgetBanner,
                                }}
                                titleText={bookingSuccessBanner?.dealPurchaseStatus}
                                titleTextColor={bookingSuccessBanner?.dealPurchaseStatusColor}
                                bookingStatusText={bookingStatus}
                                cta={bookingSuccessBanner?.cta}
                                subscriptionMeta={{
                                    title: bookingSuccessBanner?.titleText || "",
                                    subtitle: bookingSuccessBanner?.subtitle || "",
                                }}
                                girfLogo={bookingSuccessBanner?.girfLogo}
                            />
                        )}
                        {bookingStatus && isEventReservation && (
                            <EventBookingStatusBanner
                                bookingStatus={bookingStatus}
                                bookingId={bookingId}
                                qrCode={qrCode}
                                bookingStatusText={bookingSuccessBanner?.dealPurchaseStatus}
                                bookingStatusBannerSubtitle={bookingStatusBannerSubtitle}
                                partialPaymentsConfig={{
                                    isPartialPayment,
                                    payCta: completePaymentCta
                                        ? { ...completePaymentCta, onPaymentCtaClick: handlePaymentCtaClick }
                                        : null,
                                }}
                            />
                        )}
                        {!isEmpty(commsTextBannerProps) && (
                            <CommsTextBannerWrapper>
                                <CommsTextBanner {...commsTextBannerProps} />
                            </CommsTextBannerWrapper>
                        )}

                        {isEventReservation ? (
                            <TicketDetailsWrapper data-testid={SUCCESS_PAGE_TEST_IDS.TABLE_BOOKING_INFO}>
                                <HeaderTicketWrapper>
                                    <Header title={ticketTitle} subTitle={restaurantDetails?.fullAddress || ""} />
                                    {ticketDetailsData && (
                                        <CartItemListRenderer
                                            date={ticketDateTitle}
                                            time={ticketTimeTitle}
                                            itemCountTitle={ticketCountTitle}
                                            itemList={ticketDetailsData?.map((item) => ({
                                                ...item,
                                                quantity: item.quantity || 0,
                                            }))}
                                            handleInfoClick={handleInfoClick}
                                            showQuantity={true}
                                            enableSoldOutComs={true}
                                        />
                                    )}
                                </HeaderTicketWrapper>
                                {!isEmpty(savingCards) ? (
                                    <SavingsCard title={savingCards.title} cards={savingCards.cards} isScenesFlow />
                                ) : null}
                                <WhatToDoNext isPartialPayment={isPartialPayment} />
                                <HalfCardPortalContainer
                                    id="half-card-container"
                                    showPassDetailsCard={showPassDetailsCard}
                                />
                                <HalfCard
                                    isOpen={showPassDetailsCard}
                                    closeOnOverlayClick
                                    onClose={hidePassDetailsCard}
                                    positionFromBottom={safeAreaBottomPx}
                                    backgroundColor="background_Secondary"
                                    root="#half-card-container"
                                >
                                    <HalfCardContent
                                        heading={halfCardData?.heading || ""}
                                        subheadings={halfCardData?.subheadings || []}
                                        handleClose={hidePassDetailsCard}
                                    ></HalfCardContent>
                                </HalfCard>
                            </TicketDetailsWrapper>
                        ) : (
                            <InfoTicketWrapper data-testid={SUCCESS_PAGE_TEST_IDS.TABLE_BOOKING_INFO}>
                                <TableBookingInfoTicket
                                    bookingDetails={{
                                        ...bookingDetails,
                                        statusColor: getColorCode(bookingDetails.status),
                                    }}
                                    restaurantDetails={restaurantDetails}
                                    offerDetails={ticketAttributes?.offerDetails}
                                    showDownloadCta={ticketAttributes?.showDownloadCta}
                                    downloadAppBannerProps={APP_BANNER_PROPS}
                                    analyticsEventsForDownloadCta={{
                                        impressionEvent: onImpressionDownloadCTABookingSuccess,
                                        clickEvent: onClickDownloadCTABookingSuccess,
                                    }}
                                    restaurantActions={{
                                        callAction: shouldShowCallAction
                                            ? () => {
                                                  navigateToCallIntent(restaurantDetails?.phoneNumber as string);
                                              }
                                            : undefined,
                                        locationAction: shouldShowLocationAction
                                            ? () => {
                                                  navigateToLocation(
                                                      restaurantDetails?.latitude,
                                                      restaurantDetails?.longitude,
                                                      restaurantDetails?.fullAddress,
                                                  );
                                              }
                                            : undefined,
                                    }}
                                    ctas={transformedCtas}
                                    {...getConditionalProps(rewardsWidget, ctaSubTextMeta, whatToDoNextSrc)}
                                />
                            </InfoTicketWrapper>
                        )}
                        {calloutsBannerVisibilityState.showCalloutsBanner && (
                            <CalloutsBanner calloutsBanner={calloutsBanner} />
                        )}
                        {!isEventReservation && !isEmpty(rewardsPoolWidget) && (
                            <DinecashPoolBanner
                                data-testid={SUCCESS_PAGE_TEST_IDS.DINECASH_POOL_BANNER}
                                fit="contain"
                                cloudinaryId={rewardsPoolWidget?.imageId || ""}
                                alt="DineCash Pooling Banner"
                            />
                        )}
                        {rewardsWidget && <FooterImage src={whatToDoNextSrc || ""} />}
                        {viewRestaurantCtaMeta && !isWebFlow && (
                            <ViewRestaurantCtaWrapper>
                                <SuperButton
                                    variant="fullwidth"
                                    buttonType="secondary"
                                    buttonSize="large"
                                    rightIcon={Arrow20}
                                    onClick={handleViewVenueDetails}
                                >
                                    {viewRestaurantCtaMeta.text}
                                </SuperButton>
                            </ViewRestaurantCtaWrapper>
                        )}

                        {cancelBookingMeta?.show && !isWebFlow && (
                            <CancelBookingWidget
                                ctaText={cancelBookingMeta?.text}
                                leftText={cancelBookingMeta?.leftText}
                                bookingId={bookingId}
                                reloadSuccessPage={reloadSuccessPage}
                                setSoftError={setSoftError}
                                analyticsEvents={{
                                    onClickCancelBookingPopupCta,
                                    onClickCancelBookingCta,
                                }}
                                isCheckoutFlow={isCheckoutFlow}
                                dealCategory={dealCategory}
                            />
                        )}

                        <DividerWrapper />

                        {showBillWidget && (
                            <div>
                                <BillWrapper showHorizontalMargin>
                                    <BillDetails
                                        billEntries={billDetailsInfo}
                                        title={BILL_TEXTS.BILL_DETAILS_TEXT}
                                        onImpression={onBillDetailsImpression}
                                    />
                                </BillWrapper>
                                <DividerWrapper />
                            </div>
                        )}

                        {!isEmpty(softError) && (
                            <ToastContainer>
                                <Toast
                                    message={softError}
                                    containerClass={"toast"}
                                    show
                                    autoDismiss
                                    onDismiss={resetSoftError}
                                />
                            </ToastContainer>
                        )}
                        <ListViewWrapper>
                            <ListView
                                items={getListItems(ticketAttributes)}
                                expendableType={ExpendableType.MULTIPLE}
                                background="canvas_Primary"
                            />
                        </ListViewWrapper>
                    </ContentDiv>
                </ScrollableContent>
                <Footer
                    doneClickHandler={navigateToDineoutHomePage}
                    viewHelpClickHandler={handleViewHelp}
                    showHelpCta={isCheckoutFlow}
                />
            </AnimatedScrollableContent>

            {isAppFlow && (
                <>
                    <HalfCardPortalContainerWrapper
                        id="celebration-half-card-container"
                        showPassDetailsCard={showCelebrationHalfCard}
                    />

                    <TableReservationCelebrationHalfCard
                        isOpen={showCelebrationHalfCard}
                        onClose={hideCelebrationHalfCard}
                        lottieUrl={getCloudinaryUrl(LOTTIE_ANIMATIONS.TABLE_RESERVATION_CELEBRATION) as string}
                        soundUrl={getCloudinaryUrl(LOTTIE_ANIMATIONS.TABLE_RESERVATION_CELEBRATION_SOUND) as string}
                        safeAreaBottomPx={safeAreaBottomPx}
                        root="#celebration-half-card-container"
                    />
                </>
            )}
        </DineoutWrapper>
    );
};

export default memo(TableReservationSuccessPage);
